import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { DashboardLayout, holdingAdminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileSignature,
  FileText,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Building2,
  Mail,
  Send,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HoldingContracts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: "",
    recipient_name: "",
    custom_message: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (token) {
      fetchContracts();
    }
  }, [token]);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich with project info
      const enriched = await Promise.all(res.data.map(async (contract) => {
        try {
          const projectRes = await axios.get(`${API}/projects-v3/browse/${contract.project_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return { ...contract, project: projectRes.data };
        } catch {
          return contract;
        }
      }));
      setContracts(enriched);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      toast.error("Kunne ikke laste kontrakter");
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewContract = async (projectId) => {
    try {
      const res = await fetch(`${API}/contracts-pdf/preview/client-holding/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not load contract preview");
      
      const htmlContent = await res.text();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (error) {
      console.error("Error previewing contract:", error);
      toast.error("Kunne ikke vise kontrakt");
    }
  };

  const handleDownloadContract = async (projectId, projectTitle) => {
    try {
      toast.info("Laster ned kontrakt...");
      
      const res = await fetch(`${API}/contracts-pdf/client-holding/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not download contract");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kontrakt_${projectTitle || projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Kontrakt lastet ned!");
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast.error("Kunne ikke laste ned kontrakt");
    }
  };

  const openEmailModal = (contract) => {
    setSelectedContract(contract);
    setEmailForm({
      recipient_email: "",
      recipient_name: "",
      custom_message: ""
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error("Vennligst skriv inn e-postadresse");
      return;
    }
    
    try {
      setSendingEmail(true);
      
      const res = await fetch(
        `${API}/contracts-pdf/email/client-holding/${selectedContract.project_id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(emailForm)
        }
      );
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Kunne ikke sende e-post");
      }
      
      toast.success(`Kontrakt sendt til ${emailForm.recipient_email}!`);
      setEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Kunne ikke sende e-post");
    } finally {
      setSendingEmail(false);
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    signed: "bg-green-500/20 text-green-400 border-green-500/30",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    expired: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };

  const statusIcons = {
    pending: <Clock className="w-4 h-4" />,
    signed: <CheckCircle2 className="w-4 h-4" />,
    cancelled: <XCircle className="w-4 h-4" />,
    expired: <XCircle className="w-4 h-4" />,
  };

  const filteredContracts = contracts.filter(c => {
    const matchesSearch = 
      c.contract_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.project?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  const stats = {
    total: contracts.length,
    pending: contracts.filter(c => c.status === "pending").length,
    signed: contracts.filter(c => c.status === "signed").length,
  };

  return (
    <DashboardLayout navItems={holdingAdminNavItems} title={t('holdingContracts.title')}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Kontrakter</h1>
            <p className="text-gray-400 mt-1">Oversikt over alle prosjektkontrakter</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <FileSignature className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
                <p className="text-sm text-[#94A3B8]">Totalt Kontrakter</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
                <p className="text-sm text-[#94A3B8]">Venter</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">{stats.signed}</p>
                <p className="text-sm text-[#94A3B8]">Signert</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Søk etter prosjekt eller kontrakt-ID..."
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
            />
          </div>
          <div className="flex gap-2">
            {["all", "pending", "signed", "cancelled"].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status 
                  ? "bg-[#23F7DD] text-[#050505]" 
                  : "border-white/20 text-[#94A3B8]"
                }
              >
                {status === "all" ? "Alle" : status === "pending" ? "Venter" : status === "signed" ? "Signert" : "Kansellert"}
              </Button>
            ))}
          </div>
        </div>

        {/* Contracts Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredContracts.length === 0 ? (
              <div className="text-center py-12">
                <FileSignature className="w-12 h-12 mx-auto text-[#475569] mb-4" />
                <p className="text-[#94A3B8]">Ingen kontrakter funnet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Prosjekt</TableHead>
                    <TableHead className="text-[#94A3B8]">Kontrakt ID</TableHead>
                    <TableHead className="text-[#94A3B8]">Status</TableHead>
                    <TableHead className="text-[#94A3B8]">Opprettet</TableHead>
                    <TableHead className="text-[#94A3B8]">Signert</TableHead>
                    <TableHead className="text-[#94A3B8] text-right">Handlinger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow key={contract.contract_id} className="border-white/10">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-white font-medium">
                            {contract.project?.title || `Prosjekt ${contract.project_id?.slice(0, 8)}`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-[#94A3B8] text-sm">
                        {contract.contract_id?.slice(0, 16)}...
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[contract.status] || statusColors.pending}>
                          {statusIcons[contract.status]}
                          <span className="ml-1">
                            {contract.status === "pending" ? "Venter" : 
                             contract.status === "signed" ? "Signert" : contract.status}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#94A3B8]">
                        {formatDate(contract.created_at)}
                      </TableCell>
                      <TableCell className="text-[#94A3B8]">
                        {contract.signed_at ? formatDate(contract.signed_at) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/holding/projects/${contract.project_id}`)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Se Prosjekt
                          </Button>
                          {contract.status === "signed" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePreviewContract(contract.project_id)}
                                className="text-purple-400 hover:text-purple-300"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Se Kontrakt
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadContract(contract.project_id, contract.project?.title)}
                                className="text-[#23F7DD] hover:text-[#70FFEF]"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Last ned
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEmailModal(contract)}
                                className="text-orange-400 hover:text-orange-300"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Send
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-400" />
              Send Kontrakt på E-post
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send kontrakten for <span className="text-white font-medium">{selectedContract?.project?.title}</span> til en mottaker.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">E-postadresse *</Label>
              <Input
                id="email"
                type="email"
                placeholder="mottaker@firma.no"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Mottakers navn (valgfritt)</Label>
              <Input
                id="name"
                type="text"
                placeholder="Ola Nordmann"
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({...emailForm, recipient_name: e.target.value})}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-300">Melding (valgfritt)</Label>
              <Textarea
                id="message"
                placeholder="Vennligst se vedlagt kontrakt for prosjektet..."
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({...emailForm, custom_message: e.target.value})}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[#23F7DD] min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              className="border-white/20 text-gray-400 hover:text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sender...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Kontrakt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
